import classNames from 'classnames';
import { GetStaticProps, GetStaticPropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { i18n } from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React from 'react';
import { LayoutType } from 'src/components/layout/layout';
import ContactActions from 'src/components/pages/contact/actions/actions';
import ContactList from 'src/components/pages/contact/list/list';
import ContactSection from 'src/components/pages/contact/section/section';
import Box, { HeaderTheme, HeadingElement } from 'src/components/ui-kit/box/box';
import { IconVariant } from 'src/components/ui-kit/icon/icon';
import { wrapper } from 'src/store';
import styles from './contact.module.scss';
import { configuration } from '../../configurations/configuration';
import Head from 'next/head';

const classesContext = classNames.bind(styles);

const ContactPage = (): JSX.Element => {
  const { t } = useTranslation(['components:pages:contact']);

  const emailList = [
    { value: 'support@usmineralexchange.com', label: t('TEXT_FOR_SELLER_BUYER_QUESTIONS') },
    { value: 'terrac@usmineralexchange.com', label: t('TEXT_SALES_AND_MARKETING_MANAGER') },
    { value: 'gphelan@usmineralexchange.com', label: t('TEXT_CEO') }
  ];

  const phoneList = [
    { value: '888-833-3509', label: t('TEXT_TOLL_FREE_PHONE_NUMBER') },
    { value: '888-645-7011', label: t('TEXT_TOLL_FREE_FAX_NUMBER') }
  ];

  return (
    <>
      <Head>
        <title>US Mineral Exchange | Contact us</title>
        <meta
          name='description'
          content='Need to get in touch with US Mineral Exchange? Use one of the methods found here and we will respond within 24 hours.'
        />
        <link
          rel='canonical'
          href={`${process.env.NEXT_PUBLIC_URL}/contact`} />
      </Head>
      <Box
        heading={t('TEXT_CONTACT_US')}
        headingTheme={HeaderTheme.DARK}
        headingElement={HeadingElement.H1}
        className={styles.contact}
      >
        <div className={styles.contact_info}>
          <div className={classesContext(['row', styles.contact_row])}>
            <div className={classesContext(['col__50 col__mobile__100', styles.contact_col])}>
              <ContactSection
                text={t('TEXT_EMAIL')}
                iconVariant={IconVariant.EMAIL}>
                <ContactList
                  items={emailList}
                  hrefProtocol={'mailto'} />
              </ContactSection>
              <ContactSection
                text={t('TEXT_PHONE_AND_FAX')}
                iconVariant={IconVariant.PHONE}>
                <ContactList
                  items={phoneList}
                  hrefProtocol={'tel'} />
              </ContactSection>
              <ContactSection
                text={t('TEXT_MAIL')}
                iconVariant={IconVariant.LOCATION}>
                <span>{t('TEXT_US_MINERAL_EXCHANGE')}</span>
                <address className={styles.contact_address}>
                  2448 East 81st Street, Suite 2046 <br />
                  Tulsa, Oklahoma 74137
                </address>
              </ContactSection>
            </div>
            <div
              className={classesContext(['col__50 col__mobile__100', styles.contact_col, styles.contact_col__inquiry])}
            >
              <ContactSection
                text={t('TEXT_WEB_INQUIRY')}
                iconVariant={IconVariant.PLANET}>
                <p>{t('TEXT_CONTACT_US_MINERAL_EXCHANGE_THROUGH_OUR_OLINE_WEB_FORM')}</p>
                <ContactActions />
              </ContactSection>
            </div>
          </div>
        </div>
      </Box>
    </>
  );
};

ContactPage.layout = LayoutType.ACCOUNT;

export default ContactPage;

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(
  () => async ({ locale }: GetStaticPropsContext) => {
    return {
      props: {
        ...(await serverSideTranslations(locale ?? i18n.defaultLocale, [
          'common',
          'components:layout',
          'components:pages:contact'
        ]))
      },
      revalidate: configuration.revalidation.timeSec
    };
  }
);
